import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { reduxForm, change } from 'redux-form';
import { Title } from '@app/src/common/Title';
import {
  TypeVehicle,
  TypeOperation,
  FormActions,
  TitleVehicle,
  Description,
  InputTitle,
  clearBrandVehicleSelected
} from '@app/src/views/StepOne/form';
import { CATEGORIES, CATEGORY } from '@app/src/config/categories';
import { setPrintGeneral, GeneralModel, cleanPrintGeneral } from '@app/src/views/StepTwo/channelPublication';
import { isClientEnv } from '@app/src/utils/validations';
import { FormWrapper } from './styled';
import { TYPE_VEHICLE } from '@app/src/config/stepOne';

const isClient = isClientEnv();

interface Props {
  handleSubmit?: Function;
}

export const Form: React.FC<Props> = ({ handleSubmit }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { packageId } = useParams();

  const currentOperation = useSelector(state => state.form.stepOne.values.operation);
  const currentTypeVehicle = useSelector(state => state.form.stepOne.values.typeVehicle);
  const currentModelVehicleName = useSelector(state => state.form.stepOne.values.modelVehicleName);
  const currentYearVehicle = useSelector(state => state.form.stepOne.values.yearVehicle);
  const currentTitle = useSelector(state => state.form.stepOne.values.title);
  const print = isClient && JSON.parse(sessionStorage.getItem('print'));
  const currentDescription = useSelector(state => state.form.stepOne.values.description);
  const currentOperationName = useSelector(state => state.form.stepOne.values.operationName);
  const currentBrandVehicleName = useSelector(state => state.form.stepOne.values.brandVehicleName);
  const currentTypeVehicleName = useSelector(state => state.form.stepOne.values.typeVehicleName);
  const printTypeVehicle = useSelector(state => state.print.general.typeVehicle);
  const typeUserAction = useSelector(state => state.session.user.pasarella);
  const categoryId = useSelector(state => state.print.general.category);
  const categoryConfig = CATEGORIES[CATEGORY[categoryId]];

  useEffect(() => {
    if (currentDescription && isClient) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      }
    }
  }, [currentDescription]);

  useEffect(() => {
    if (currentTypeVehicle !== printTypeVehicle) {
      dispatch(change('stepOne', 'title', ''));
    }
  }, [currentTypeVehicle]);

  const handleBeforeUnload = (e) => {
    if (isClient) {
      sessionStorage.setItem('print', JSON.stringify({ general: { category: categoryId } }));
      e.returnValue = '¿Desea descartar cambios?';
    }
  };

  const handleClickSubmit = (data: GeneralModel) => {
    const updatePrint = createPrintStorage(data);
    isClient && sessionStorage.setItem('print', JSON.stringify(updatePrint));
    dispatch(setPrintGeneral(updatePrint));
    const eventLabelText = `${currentOperationName}|${currentTypeVehicleName}|${currentBrandVehicleName || 0}|${currentModelVehicleName || 0}|${currentYearVehicle || 0}`;
    pushDatalayer(
      categoryConfig.name,
      eventLabelText
    );

    if (typeUserAction === 'consume-print') {
      history.push(categoryConfig.paths.consumePackage.stepTypePublication + (packageId && `/${packageId}`));
    }
    if (typeUserAction === 'purchase-print') {
      history.push(categoryConfig.paths.purchasePrint.stepTypePublication);
    }
  };

  const createPrintStorage = (data) => {
    const objPrint = {};

    if (print && print.hasOwnProperty('printsByChannel')) {
      objPrint['printsByChannel'] = print.printsByChannel;
    } else {
      objPrint['printsByChannel'] = {};
    }
    objPrint['general'] = data;
    return objPrint;
  };

  const handleClickReset = (e) => {
    e.preventDefault();
    if (currentDescription) {
      const isDiscardChanges = confirm('¿desea descartar cambios?');
      if (!isDiscardChanges) {
        return;
      }
    }
    history.goBack();
    sessionStorage.removeItem('print');
    dispatch(cleanPrintGeneral());
    dispatch(clearBrandVehicleSelected());
  };

  const pushDatalayer = (categoryName: string, eventLabelText: string) => {
    if (isClient && (window as any).dataLayer) {
      (window as any).dataLayer.push({
        'event': 'EVClasificados',
        'eventCategory': 'Posting',
        'eventAction': `Paso1 - ${categoryName}`,
        'eventLabel': eventLabelText,
        'eventValue': 1
      })
    }
  };

  return (
    <FormWrapper>
      <Title description="Ingrese los datos de contacto en la descripción del aviso.">Datos del aviso</Title>
      <TypeOperation />
      {
        currentOperation &&
        <TypeVehicle operation={currentOperation} />
      }
      {
        (currentOperation && currentTypeVehicle) &&
        (currentTypeVehicle === TYPE_VEHICLE.MAQUINAS_MOTORES ?
          <InputTitle placeholder="Ej. Remato" />
          :
          <TitleVehicle typeVehicle={currentTypeVehicle} />)
      }
      {
        currentTitle &&
        <Description
          includeWords={12}
          extraWords={(typeUserAction === 'purchase-print') ? 8 : 0}
        />
      }
      <FormActions
        showButtonNext={currentOperation && currentTitle}
        handleButtonNext={handleSubmit(handleClickSubmit)}
        handleButtonPrev={handleClickReset}
      />
    </FormWrapper>
  )
};

const FormVehicleHoc = reduxForm({
  form: 'stepOne',
  initialValues: {
    category: null,
    operation: null,
    operationName: '',
    typeVehicle: null,
    typeVehicleName: '',
    modelVehicle: null,
    modelVehicleName: '',
    modelAdecsys: null,
    brandVehicle: null,
    brandVehicleName: '',
    brandAdecsys: null,
    yearVehicle: '',
    title: '',
    description: '',
    extraWords: null
  }
})(Form);

export { FormVehicleHoc as FormVehicle };
